import {
  type ComponentPropsWithRef,
  type ElementType,
  type ReactNode,
} from "react";
import styles from "./Text.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";

export type Variant =
  | "tera"
  | "giga"
  | "mega"
  | "kilo"
  | "hecto"
  | "deca"
  | "base"
  | "deci"
  | "micro"
  | "centi";

export type Weight = "medium" | "bold";

export type Color =
  | "neutral"
  | "muted"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "white"
  | "violet"
  | "orange"
  | "primary";

type TextProps<T extends ElementType = "p"> = {
  children?: ReactNode;
  variant?: Variant;
  weight?: Weight;
  color?: Color;
  background?: boolean;
  as?: T;
  className?: string;
} & Omit<ComponentPropsWithRef<T>, "children" | "className">;

export default function Text<T extends ElementType = "p">({
  children,
  variant = "base",
  weight,
  color,
  background,
  as,
  className,
  ...props
}: TextProps<T>) {
  const Tag = as || "p";
  const weightSuffix = weight ? `-${weight}` : "";
  const variantClass = styles[`${variant}${weightSuffix}`] || styles[variant];
  const backgroundClass = background
    ? styles[`background-${color}`]
    : undefined;
  // Semantic colors are tuned for badge contrast (dark text on a tinted
  // background). As standalone text — no background — a color falls back to its
  // lighter "medium" shade when one is defined (e.g. `.successMedium`).
  const mediumClass = color ? styles[`${color}Medium`] : undefined;
  const colorClass =
    !background && mediumClass ? mediumClass : color && styles[color];

  return (
    <Tag
      className={classnames(
        styles.text,
        variantClass,
        colorClass,
        backgroundClass,
        className,
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}
